import {
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import Clear from '@mui/icons-material/Clear';
import Search from '@mui/icons-material/Search';
import React, { useState } from 'react';

import FilterListIcon from '@mui/icons-material/FilterList';
import { logEventAnalytic } from 'src/utilities/logAnalytics';
import { useSearch } from 'src/store';
import useTranslation from 'next-translate/useTranslation';
import Filters from '../Filters';
import SearchControlsProps from './SearchControls.types';
import theme from '../../../themes/theme';
import {
  ButtonsContainer,
  FilterButton,
  SearchContainer,
} from './SearchControls.styles';

export const SearchControls = (props: SearchControlsProps) => {
  const { handleSearch, overrides, size } = props;
  const [isFiltersOpen, setIsFiltersOpen] = useState(true);
  const toggleFilters = () => {
    setIsFiltersOpen(!isFiltersOpen);
  };
  const { t } = useTranslation('search');

  const { searchTerm, setSearchTerm } = useSearch();

  const invokeSearch = () => {
    logEventAnalytic({
      category: 'Search',
      action: 'Searched for an app using the search bar - microsite',
      label: `${searchTerm}`,
    });
    handleSearch(searchTerm);
  };

  return (
    <>
      <SearchContainer>
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <TextField
            sx={{ backgroundColor: theme.palette.common.white }}
            size={size}
            fullWidth
            onChange={(event) => {
              setSearchTerm(event.target.value);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                invokeSearch();
              }
            }}
            value={searchTerm ?? ''}
            placeholder={overrides?.placeholderText ?? t('placeholder')}
            inputProps={{
              maxLength: 100,
              'data-testid': 'search-input',
              'aria-label': t('searchInputLabel'),
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              sx: {
                fontSize: theme.typography.fontSize,
                paddingRight: `${theme.spacing(6)} !important`,
              },
            }}
          />

          <Box
            sx={{
              position: 'absolute',
              right: theme.spacing(0.5),
              display: searchTerm && searchTerm?.length > 0 ? 'block' : 'none',
            }}
          >
            <IconButton
              aria-label={t('clearSearchLabel')}
              onClick={() => setSearchTerm('')}
              sx={{ padding: theme.spacing(1) }}
              size={size}
            >
              <Clear />
            </IconButton>
          </Box>
        </Box>

        <ButtonsContainer>
          <FilterButton
            data-testid="toggle-filters-button"
            variant="contained"
            endIcon={<FilterListIcon />}
            disableElevation
            className={isFiltersOpen ? 'showFilters' : ''}
            onClick={toggleFilters}
            fullWidth
          >
            {t('filterButton')}
          </FilterButton>

          <Button
            color="primary"
            data-testid="search-button"
            variant="contained"
            onClick={invokeSearch}
            fullWidth
            sx={{
              [theme.breakpoints.up('sm')]: {
                minWidth: '180px',
              },
            }}
          >
            {overrides?.buttonText ?? t('searchButton')}
          </Button>
        </ButtonsContainer>
      </SearchContainer>
      <Collapse orientation="vertical" in={isFiltersOpen}>
        <Box mt={1.5}>
          <Filters />
        </Box>
      </Collapse>
    </>
  );
};

export default SearchControls;
